import { Isuccess } from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { FlyingMailClient } from '../../utils/flying-mail-client';

/**
 * Interface representing a super mail permission.
 */
export interface ISuperMailPermission {
  id: number;
  userId: string | number;
  mainAccess: boolean;
  read: boolean;
  write: boolean;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Class representing a super mail permission.
 */
export class MailSuperPermission {
  /**
   * Base URL for mail super permission API endpoints.
   */
  static baseUrl = '/super-permissions';

  /**
   * The ID of the permission.
   */
  id: number;

  /**
   * The ID of the user associated with the permission.
   */
  userId: string | number;

  /**
   * Whether the user has main access to the mail.
   */
  mainAccess: boolean;

  /**
   * Whether the user has read access to the mail.
   */
  read: boolean;

  /**
   * Whether the user has write access to the mail.
   */
  write: boolean;

  /**
   * The creation date of the permission.
   */
  createdAt: Date;

  /**
   * The update date of the permission.
   */
  updatedAt: Date;

  constructor(data: ISuperMailPermission) {
    this.id = data.id;
    this.userId = data.userId;
    this.mainAccess = data.mainAccess;
    this.read = data.read;
    this.write = data.write;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
  }

  /**
   * Retrieves mail super permissions for a specific user.
   *
   * @param userId - The ID of the user to retrieve permissions for.
   * @returns A Promise that resolves to a new MailSuperPermission instance.
   */
  static async getByUser(userId: string | number) {
    const observer$ = FlyingMailClient.ehttp
      .makeGet<ISuperMailPermission>(`${MailSuperPermission.baseUrl}/user/${userId}`);
    const data = await lastValueFrom(observer$);

    return new MailSuperPermission(data);
  }

  /**
   * Adds or updates mail super permissions for a specific user.
   *
   * @param permissions - The permissions to add or update.
   * @returns A Promise that resolves to an Isuccess instance.
   */
  static addUpdate(permissions: Omit<ISuperMailPermission, 'id' | 'createdAt' | 'updatedAt'>) {
    const observer$ = FlyingMailClient.ehttp
      .makePut<Isuccess>(`${MailSuperPermission.baseUrl}/${permissions.userId}`, permissions);

    return lastValueFrom(observer$);
  }

  /**
   * Removes the mail super permission.
   *
   * @returns A Promise that resolves to an Isuccess instance.
   */
  remove() {
    return FlyingMailClient.ehttp
      .makeDelete<Isuccess>(`${MailSuperPermission.baseUrl}/${this.userId}`);
  }
}
